import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SubmitHandler } from 'react-hook-form';

import { useLazyGetTripsExportAsCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { DetailsFormFields } from '../DetailsFormFields';
import { DetailsForm, useDetailsForm } from '../useDetailsForm';

interface EvertransitDetailsExportProps {
  onClose: () => void;
}

export function EvertransitDetailsExport({
  onClose,
}: EvertransitDetailsExportProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [trigger] = useLazyGetTripsExportAsCsvQuery();

  const { control, handleSubmit } = useDetailsForm();

  const handleDownloadTrips = useCallback<SubmitHandler<DetailsForm>>(
    async (fields) => {
      try {
        setError(undefined);
        setLoading(true);

        // Trigger will cause a blob download, see emptyApi
        await trigger({
          rangeStart: fields.startDate?.startOf('day').toISO() ?? '',
          rangeEnd: fields.endDate?.endOf('day').toISO() ?? '',
          externalTripIds:
            fields.externalTripIdOptions
              ?.map((option) => option.value)
              .join(',') ?? '',
          outputSet: 'evertransit',
          format: 'csv',
        }).unwrap();
        onClose();
      } catch (e) {
        const errMsg = `There was an error downloading the data. ${formatServerError(
          e
        )}`;
        setError(errMsg);
        setLoading(false);
      }
    },
    [onClose, trigger]
  );

  return (
    <div>
      <h1 className="text-2xl">Evertransit details download</h1>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      <DetailsFormFields
        control={control}
        includeDateRange
        includeExternalTripIds
      />
      <div className="flex flex-row justify-between gap-4 pt-6">
        <div>
          <Button disabled={loading} onClick={onClose}>
            Cancel
          </Button>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            dataTestId="download-csv"
            disabled={loading}
            loading={loading}
            variant="primary"
            onClick={handleSubmit(handleDownloadTrips)}
          >
            {loading ? 'Exporting' : 'Export as CSV'}
          </Button>
        </div>
      </div>
    </div>
  );
}
